import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

type Props = {
  title: string;
  titleActions?: React.ReactElement;
  subtitle?: string;
  actions?: React.ReactElement;
  showIsOptional?: boolean;
  variant?: "elevation" | "outlined";
  size?: "default" | "compact";
  lockedContent?: string;
  noPadding?: boolean;
} & (
  | {
      content: React.ReactElement;
    }
  | {
      children: React.ReactElement;
    }
);

const CardSection = (props: Props) => {
  const {
    actions,
    title,
    titleActions,
    subtitle,
    variant = "elevation",
    size = "default",
    showIsOptional = false,
    lockedContent,
    noPadding = false,
  } = props;

  const content = "content" in props ? props.content : props.children;
  const isCompact = size === "compact";

  const isLocked = lockedContent !== undefined;
  let headerAction = null;

  let lockedItem = null;
  if (isLocked) {
    lockedItem = (
      <Box
        sx={{
          position: "absolute",
          top: isCompact ? "12px" : "16px",
          right: isCompact ? "12px" : "16px",
          backgroundColor: "rgba(255, 255, 255, 0.5)",
          zIndex: 10,
        }}
      >
        <Tooltip title={lockedContent} arrow placement="right">
          <LockIcon fontSize={isCompact ? "small" : "medium"} />
        </Tooltip>
      </Box>
    );
  } else if (showIsOptional) {
    headerAction = (
      <Typography variant="overline" pr={isCompact ? 1 : 2}>
        OPTIONAL
      </Typography>
    );
  }

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        borderRadius: isCompact ? "6px" : "8px",
        marginBottom: isCompact ? "16px" : "32px",
        border: "1px solid",
        borderColor: "var(--intern-border-default, #DBDCDD)",
        position: "relative",
        bgcolor: "var(--intern-bg-primary, #fff)",
        overflow: "hidden",
      }}
      variant={variant}
    >
      <CardHeader
        sx={{
          borderBottom: "1px solid",
          borderColor: "var(--intern-border-default, #DBDCDD)",
          opacity: isLocked ? "40%" : undefined,
          pointerEvents: isLocked ? "none" : undefined,
          py: isCompact ? 1.5 : 2,
          px: { xs: 2, md: isCompact ? 2 : 3 },
        }}
        title={
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={{ xs: 1, sm: 0 }}
            mb={subtitle ? (isCompact ? "4px" : "8px") : 0}
          >
            <Typography
              variant={isCompact ? "subtitle1" : "h6"}
              sx={{
                color: "var(--intern-text-primary, inherit)",
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              {title}
            </Typography>
            {titleActions}
          </Stack>
        }
        subheader={
          subtitle ? (
            <Typography
              variant={isCompact ? "caption" : "body2"}
              sx={{ color: "var(--intern-text-tertiary, text.secondary)" }}
            >
              {subtitle}
            </Typography>
          ) : undefined
        }
        action={headerAction}
      />
      <CardContent
        sx={{
          px: noPadding ? 0 : { xs: 2, md: isCompact ? 2 : 3 },
          py: noPadding ? 0 : isCompact ? 1.5 : 3,
          opacity: isLocked ? "40%" : undefined,
          pointerEvents: isLocked ? "none" : undefined,
          overflowX: "auto",
          "&:last-child": {
            pb: noPadding ? 0 : isCompact ? 1.5 : 3,
          },
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: "100%" }}>{content}</Box>
      </CardContent>
      {actions && (
        <CardActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: isCompact ? 1.5 : 2,
            borderTop: "1px solid",
            borderColor: "var(--intern-border-default, #DBDCDD)",
            opacity: isLocked ? "40%" : undefined,
            pointerEvents: isLocked ? "none" : undefined,
          }}
        >
          {actions}
        </CardActions>
      )}
      {lockedItem}
    </Card>
  );
};

export default CardSection;
